import { useMemo, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Megaphone, Search, Pencil, AlertTriangle } from "lucide-react";
import { useWebsitePagesAdmin, useUpdateWebsitePage, type WebsitePageRow } from "@/hooks/admin/useWebsitePagesAdmin";
import type { CtaMode, WebsitePageConfig } from "@/types/website-pages";
import { getErrorMessage } from "@/lib/error-message";

export default function MarketingPagesAdminPage() {
  const [search, setSearch] = useState("");
  const [onlyUnpublished, setOnlyUnpublished] = useState(false);
  const [editing, setEditing] = useState<WebsitePageRow | null>(null);
  const { data: pages = [], isLoading } = useWebsitePagesAdmin();

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return pages.filter((p) => {
      if (onlyUnpublished && p.is_published) return false;
      if (!term) return true;
      return p.slug.toLowerCase().includes(term) || p.title.toLowerCase().includes(term);
    });
  }, [pages, search, onlyUnpublished]);

  return (
    <DashboardLayout>
      <div className="space-y-6 p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
            <Megaphone className="h-6 w-6" /> Marketing Pages
          </h1>
          <p className="text-sm text-muted-foreground">
            Edit hero copy and CTAs on the public service-type landing pages without a deploy.
            Changes reach the TC marketing site on its next fetch.
          </p>
        </div>

        <Card>
          <CardContent className="pt-5 space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search slug or title…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <Switch checked={onlyUnpublished} onCheckedChange={setOnlyUnpublished} /> Only unpublished
              </label>
              <span className="text-xs text-muted-foreground ml-auto">{filtered.length} pages</span>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Page</TableHead>
                    <TableHead>CTA</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && (
                    <TableRow><TableCell colSpan={4} className="text-sm text-muted-foreground">Loading…</TableCell></TableRow>
                  )}
                  {!isLoading && filtered.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="text-sm text-muted-foreground text-center py-8">No pages match.</TableCell></TableRow>
                  )}
                  {filtered.map((p) => {
                    const cfg = p.page_config;
                    const label = cfg.cta_label ?? cfg.cta_text ?? "";
                    const mode = (cfg.cta_mode ?? "form") as CtaMode;
                    const misconfigured = mode === "link" && !cfg.cta_url;
                    return (
                      <TableRow key={p.id}>
                        <TableCell>
                          <div className="font-medium">{p.title}</div>
                          <div className="text-xs text-muted-foreground font-mono">/{p.slug}</div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{label || <span className="text-muted-foreground italic">(unset)</span>}</span>
                            <Badge variant="outline" className="text-xs capitalize">{mode}</Badge>
                            {misconfigured && (
                              <span className="inline-flex items-center gap-1 text-xs text-destructive" title="mode=link but no cta_url">
                                <AlertTriangle className="h-3 w-3" /> No URL
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={p.is_published ? "default" : "secondary"} className="text-xs">
                            {p.is_published ? "Published" : "Draft"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(p)} aria-label={`Edit ${p.title}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <MarketingPageEditor page={editing} onClose={() => setEditing(null)} />
    </DashboardLayout>
  );
}

interface FormState {
  title: string;
  meta_description: string;
  headline: string;
  subheadline: string;
  description: string;
  cta_label: string;
  cta_mode: CtaMode;
  cta_url: string;
  widgetsJson: string;
}

const URL_RE = /^https?:\/\/.+/;

function MarketingPageEditor({ page, onClose }: { page: WebsitePageRow | null; onClose: () => void }) {
  const update = useUpdateWebsitePage();
  const [form, setForm] = useState<FormState | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [widgetsError, setWidgetsError] = useState<string | null>(null);

  useMemo(() => {
    if (!page) {
      setForm(null);
      return;
    }
    const cfg = page.page_config;
    setForm({
      title: page.title,
      meta_description: page.meta_description ?? "",
      headline: cfg.headline ?? "",
      subheadline: cfg.subheadline ?? "",
      description: cfg.description ?? "",
      cta_label: cfg.cta_label ?? cfg.cta_text ?? "",
      cta_mode: (cfg.cta_mode ?? "form") as CtaMode,
      cta_url: cfg.cta_url ?? "",
      widgetsJson: JSON.stringify(
        (cfg as { widgets?: unknown[] }).widgets ?? [],
        null,
        2
      ),
    });
    setUrlError(null);
    setWidgetsError(null);
  }, [page]);

  if (!page || !form) return null;

  const handleSave = async () => {
    if (form.cta_mode === "link") {
      if (!form.cta_url.trim()) {
        setUrlError("URL is required when CTA mode is 'link'.");
        return;
      }
      if (!URL_RE.test(form.cta_url.trim())) {
        setUrlError("URL must start with http:// or https://.");
        return;
      }
    }
    setUrlError(null);

    // Parse widgets JSON. Empty string = keep whatever widgets the row has;
    // non-empty must parse cleanly and be an array, otherwise we block save.
    let parsedWidgets: unknown[] | undefined;
    const raw = form.widgetsJson.trim();
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
          setWidgetsError("Widgets must be a JSON array (e.g. [] or [{...}]).");
          return;
        }
        parsedWidgets = parsed;
      } catch (e) {
        setWidgetsError(getErrorMessage(e, "Invalid JSON."));
        return;
      }
    }
    setWidgetsError(null);

    const nextConfig: WebsitePageConfig & { widgets?: unknown[] } = {
      ...page.page_config,
      headline: form.headline.trim() || undefined,
      subheadline: form.subheadline.trim() || undefined,
      description: form.description.trim() || undefined,
      cta_label: form.cta_label.trim() || undefined,
      cta_mode: form.cta_mode,
      cta_url: form.cta_mode === "link" ? form.cta_url.trim() : null,
      // Mirror cta_label back to legacy cta_text so the TC site keeps working
      // until its consumer PR swaps the reads (expand-contract).
      cta_text: form.cta_label.trim() || undefined,
      ...(parsedWidgets !== undefined ? { widgets: parsedWidgets } : {}),
    };

    await update.mutateAsync({
      id: page.id,
      title: form.title.trim(),
      meta_description: form.meta_description.trim() || null,
      page_config: nextConfig,
    });
    onClose();
  };

  return (
    <Sheet open={!!page} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>/{page.slug}</SheetTitle>
        </SheetHeader>

        <div className="space-y-5 py-4">
          <div className="space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">SEO</div>
            <div className="space-y-1.5">
              <Label className="text-xs">Page title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Meta description</Label>
              <Textarea rows={2} value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} />
            </div>
          </div>

          <div className="space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Copy</div>
            <div className="space-y-1.5">
              <Label className="text-xs">Headline</Label>
              <Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Subheadline</Label>
              <Input value={form.subheadline} onChange={(e) => setForm({ ...form, subheadline: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>

          <div className="space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">CTA</div>
            <div className="space-y-1.5">
              <Label className="text-xs">Button label</Label>
              <Input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} placeholder="Get Your Custom Menu" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Mode</Label>
              <RadioGroup
                value={form.cta_mode}
                onValueChange={(v) => setForm({ ...form, cta_mode: v as CtaMode })}
                className="flex gap-4"
              >
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <RadioGroupItem value="form" id="cta-form" /> Open inline form
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <RadioGroupItem value="link" id="cta-link" /> Redirect to URL
                </label>
              </RadioGroup>
            </div>
            {form.cta_mode === "link" && (
              <div className="space-y-1.5">
                <Label className="text-xs">Destination URL</Label>
                <Input
                  value={form.cta_url}
                  onChange={(e) => { setForm({ ...form, cta_url: e.target.value }); setUrlError(null); }}
                  placeholder="https://app.tristanoscatering.ca/plan"
                />
                {urlError && <p className="text-xs text-destructive">{urlError}</p>}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Widgets (advanced)
            </div>
            <p className="text-xs text-muted-foreground">
              Raw <code>page_config.widgets[]</code> JSON — escape hatch until the full block editor
              (<a href="https://github.com/franklyadvisors/franklycatering/issues/27" target="_blank" rel="noopener noreferrer" className="underline">#27</a>)
              ships. Must be a JSON array. A pricing card can carry an optional <code>"slug"</code>
              (a short unique name, e.g. <code>"silver"</code>) so leads from its button show which card was clicked.
            </p>
            <Textarea
              rows={12}
              value={form.widgetsJson}
              onChange={(e) => { setForm({ ...form, widgetsJson: e.target.value }); setWidgetsError(null); }}
              placeholder="[]"
              className="font-mono text-base md:text-xs"
              spellCheck={false}
            />
            {widgetsError && <p className="text-xs text-destructive">{widgetsError}</p>}
          </div>
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={onClose} disabled={update.isPending}>Cancel</Button>
          <Button onClick={handleSave} disabled={update.isPending || !form.title.trim()}>
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
